import React, { useMemo, useState } from 'react'
import { Box, Container, Stack } from '@mui/material'
import Documents from './Documents'
import PC300Mesures from './PC300Mesures'
import Biologicals from './Biologicals'
import PatientInfo from './PatientInfo'
import { LiveTranscriptionSection } from '@/features/live-transcription'
import { useReportLicenseEnabled } from '@/features/report'
import { DiagnosticRTCProvider, DiagnosticRTCProviderProps, DiagnosticRTCServiceType } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import DiagnosticStethoscope from './DiagnosticStethoscope'
import DiagnosticMedicalVideo from './DiagnosticMedicalVideo'
import { BottomBarLocalConsultation } from '../components/BottomBarLocalConsultation'
import { useTranslation } from 'react-i18next'
import Biotech from '@mui/icons-material/Biotech'
import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'
import StethoscopeIcon from '@/components/ui/icons/Stethoscope'
import ContactPageIcon from '@mui/icons-material/ContactPage'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import MedicalVideoIcon from '@/components/ui/icons/MedicalVideo'
import { CustomDevice } from '@/core/models/custom-device.model'
import { PatientConsultations } from '@/features/patient/patient-consultations'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { skipToken } from '@reduxjs/toolkit/query'

type MenuItemType = {
  id: string
  icon: any
  label: string
  isNested?: boolean
}

interface MobileConsultationPageProps {
  consultationId: string | undefined
  menuItems: MenuItemType[]
  stethoscopeAttendeeInfo: Record<string, any> | null
  stethoscopeMeetingInfo: Record<string, any> | null
  audioPeerjsConfig: DiagnosticRTCProviderProps['config'][DiagnosticRTCServiceType.Peerjs]
  videoPeerjsConfig: DiagnosticRTCProviderProps['config'][DiagnosticRTCServiceType.Peerjs]
  rpiConfig:
    | {
        meetingID: string
        serverUrl: string
      }
    | undefined
  customDevices: (CustomDevice & { available?: boolean | undefined })[]
  medicalVideoAttendeeInfo: Record<string, any> | null
  medicalVideoMeetingInfo: Record<string, any> | null
}

const MobileConsultationPage = ({
  consultationId,
  menuItems,
  stethoscopeAttendeeInfo,
  stethoscopeMeetingInfo,
  audioPeerjsConfig,
  videoPeerjsConfig,
  rpiConfig,
  customDevices,
  medicalVideoAttendeeInfo,
  medicalVideoMeetingInfo,
}: MobileConsultationPageProps) => {
  const { t } = useTranslation()

  const { data: consultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const hasReportsLicense = useReportLicenseEnabled()

  const mobileMenu = useMemo(
    () => [
      {
        id: 'patientInfo',
        icon: PatientIdentityIcon,
        label: t('page.localConsultation.sidebar.patientInfo.label'),
        isNested: false,
      },
      {
        id: 'documents',
        icon: ConsultationDocumentsIcon,
        label: t('page.localConsultation.sidebar.documents.label'),
        isNested: false,
      },
      {
        id: 'consultations',
        icon: ContactPageIcon,
        label: t('page.localConsultation.sidebar.historicalConsultations.label'),
        isNested: false,
      },
      ...(hasReportsLicense
        ? [
            {
              id: 'transcription',
              icon: RecordVoiceOverIcon,
              label: t('page.liveConsultation.sidebar.transcription.label'),
            },
          ]
        : []),
      {
        id: 'mesures',
        icon: VitalParametersIcon,
        label: t('page.localConsultation.sidebar.mesures.label'),
        isNested: true,
      },
      {
        id: 'medicalVideo',
        icon: MedicalVideoIcon,
        label: t('page.liveConsultation.sidebar.medicalVideo.label'),
        isNested: true,
      },
      {
        id: 'biologicals',
        icon: Biotech,
        label: t('page.localConsultation.sidebar.biologicals.label'),
        isNested: true,
      },
      {
        id: 'stethoscope',
        icon: StethoscopeIcon,
        label: t('page.localConsultation.sidebar.stethoscope.label'),
        isNested: true,
      },
    ],
    [t, hasReportsLicense]
  )

  const [mobileSelectedMenuItem, setMobileSelectedMenuItem] = useState<string>(mobileMenu[0].id)

  return (
    <Box sx={{ flex: 1, mb: 7 }}>
      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <Container maxWidth="md">
          {mobileSelectedMenuItem === 'documents' && (
            <Stack pt={2}>
              <Documents consultationId={consultationId} />
            </Stack>
          )}
          {mobileSelectedMenuItem === 'mesures' && <PC300Mesures consultationId={consultationId} />}
          {mobileSelectedMenuItem === 'biologicals' && <Biologicals consultationId={consultationId} />}
          {mobileSelectedMenuItem === 'patientInfo' && <PatientInfo consultationId={consultationId} />}
          {mobileSelectedMenuItem === 'consultations' && consultation && (
            <Stack pt={2}>
              <PatientConsultations patientId={consultation.patient._id} />
            </Stack>
          )}
          {mobileSelectedMenuItem === 'transcription' && consultation && consultationId && (
            <LiveTranscriptionSection consultation={consultation} consultationId={consultationId} />
          )}
          {mobileSelectedMenuItem === 'stethoscope' && (
            <DiagnosticRTCProvider
              config={{
                chime: {
                  attendeeInfo: stethoscopeAttendeeInfo,
                  meetingInfo: stethoscopeMeetingInfo,
                },
                rpi: rpiConfig,
              }}
              customDevices={customDevices}
              isStreamer
              type="audio"
            >
              <DiagnosticStethoscope consultationId={consultationId} />
            </DiagnosticRTCProvider>
          )}
          {mobileSelectedMenuItem === 'medicalVideo' && (
            <DiagnosticRTCProvider
              config={{
                chime: {
                  attendeeInfo: medicalVideoAttendeeInfo,
                  meetingInfo: medicalVideoMeetingInfo,
                },
                peerjs: videoPeerjsConfig,
                rpi: rpiConfig,
              }}
              customDevices={customDevices}
              isStreamer
              type="video"
            >
              <DiagnosticMedicalVideo videoHeight="95vh" videoWidth="100vw" consultationId={consultationId} />
            </DiagnosticRTCProvider>
          )}
        </Container>
      </Box>

      <BottomBarLocalConsultation
        menuItems={menuItems}
        mobileMenu={mobileMenu}
        setMobileSelectedMenuItem={setMobileSelectedMenuItem}
        setSelectedMenuItem={setMobileSelectedMenuItem}
        selectedMenuItem={mobileSelectedMenuItem}
      />
    </Box>
  )
}

export default MobileConsultationPage
